import { useRef } from 'react';
import type { FlyerFieldConfig } from '@/types/flyer';
import { fieldToRect, moveRect, resizeRect, type Handle, type PctRect } from '@/lib/flyer-canvas';

interface Props {
  width: number;           // template px (aspect only)
  height: number;
  backgroundUrl: string | null;
  fields: FlyerFieldConfig[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChangeRect: (id: string, r: PctRect) => void;
}

const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_POS: Record<Handle, React.CSSProperties> = {
  nw: { left: -4, top: -4, cursor: 'nwse-resize' },
  n: { left: 'calc(50% - 4px)', top: -4, cursor: 'ns-resize' },
  ne: { right: -4, top: -4, cursor: 'nesw-resize' },
  e: { right: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
  se: { right: -4, bottom: -4, cursor: 'nwse-resize' },
  s: { left: 'calc(50% - 4px)', bottom: -4, cursor: 'ns-resize' },
  sw: { left: -4, bottom: -4, cursor: 'nesw-resize' },
  w: { left: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
};

export function EditorCanvas({ width, height, backgroundUrl, fields, selectedId, onSelect, onChangeRect }: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  // Drag session state lives in a ref — no re-render churn during pointermove.
  const drag = useRef<{ id: string; handle: Handle | 'move'; startX: number; startY: number; startRect: PctRect } | null>(null);

  const beginDrag = (e: React.PointerEvent, field: FlyerFieldConfig, handle: Handle | 'move') => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(field.id);
    drag.current = { id: field.id, handle, startX: e.clientX, startY: e.clientY, startRect: fieldToRect(field) };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = canvasRef.current;
    if (!d || !el) return;
    const dx = (e.clientX - d.startX) / el.clientWidth;
    const dy = (e.clientY - d.startY) / el.clientHeight;
    const next = d.handle === 'move' ? moveRect(d.startRect, dx, dy) : resizeRect(d.startRect, d.handle, dx, dy);
    onChangeRect(d.id, next);
  };

  const endDrag = () => { drag.current = null; };

  return (
    <div
      ref={canvasRef}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={() => onSelect(null)}
      style={{
        position: 'relative', width: '100%', aspectRatio: `${width} / ${height}`,
        background: '#e8e8e8', overflow: 'hidden', touchAction: 'none',
        border: '1px solid #d9d9d9', borderRadius: 4,
      }}
    >
      {backgroundUrl && (
        <img src={backgroundUrl} alt="" draggable={false}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', userSelect: 'none' }} />
      )}
      {fields.map((f) => {
        const selected = f.id === selectedId;
        return (
          <div key={f.id}
            onPointerDown={(e) => beginDrag(e, f, 'move')}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: `${f.x_percent * 100}%`, top: `${f.y_percent * 100}%`,
              width: `${f.width_percent * 100}%`, height: `${f.height_percent * 100}%`,
              border: selected ? '2px solid #1677ff' : '1px dashed rgba(22,119,255,0.6)',
              background: selected ? 'rgba(22,119,255,0.08)' : 'transparent',
              cursor: 'move', display: 'flex', alignItems: 'center', overflow: 'hidden',
              justifyContent: f.style_config?.alignment === 'left' ? 'flex-start' : f.style_config?.alignment === 'right' ? 'flex-end' : 'center',
            }}>
            <span style={{
              fontSize: 12, color: f.style_config?.color ?? '#333', whiteSpace: 'nowrap',
              fontWeight: f.style_config?.fontWeight === 'bold' ? 700 : 400,
              fontStyle: f.style_config?.isItalic ? 'italic' : 'normal',
              pointerEvents: 'none', userSelect: 'none', padding: '0 2px',
            }}>
              {f.default_text ?? f.label}
            </span>
            {selected && HANDLES.map((h) => (
              <div key={h} data-handle={h}
                onPointerDown={(e) => beginDrag(e, f, h)}
                style={{ position: 'absolute', width: 8, height: 8, background: '#1677ff', borderRadius: 2, ...HANDLE_POS[h] }} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
